/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { CannedCollectionModule } from '@coursera-web/cds-labs';

import { isUserRightToLeft } from 'js/lib/language';

import { Grid, PageGridContainer, breakpoints, typography2 } from '@coursera/cds-core';
import type { MerchandisingModuleModuleType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import getCardTakeoverProps from 'bundles/unified-description-page-common/module-renderers/upsell/getCardTakeoverProps';

export const styles = {
  promoUnitsModule: (isRtl: boolean) => css`
    display: flex;
    flex-direction: row;
    justify-content: center;

    /* Outermost container for CannedCollectionModule */
    > div {
      display: flex !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    /* Grid container for CannedCollectionModule */
    > div > div {
      padding: 0 !important;
      margin: 0;
    }

    /* Grid item wrapper for CannedCollectionModule */
    > div > div > div {
      margin: 0 !important;
      padding: 0 !important;
      gap: 0;
    }

    /* Grid item for CannedCollectionModule. Removes space between sections */

    > div > div > div > div > div > div {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
    }

    /* Card list container */
    ul {
      /* Card item */
      li {
        > a > div > div:first-of-type {
          padding-top: var(--cds-spacing-400);
          padding-bottom: var(--cds-spacing-400);
        }

        /* Card eyebrow */
        div[data-testid='eyebrow-image'] {
          margin-bottom: 0;

          > div {
            display: flex;
          }

          img {
            max-height: 11.5px !important;
          }
        }

        /* Card headline */
        h3 {
          ${typography2.subtitleLarge};
          margin-top: var(--cds-spacing-150);

          ${breakpoints.down('sm')} {
            margin-top: var(--cds-spacing-100);
          }
        }

        /* Card CTA */
        button.cds-button-ghostInvert {
          margin-top: var(--cds-spacing-50);
          padding-left: 0;
          padding-right: 0;

          .cds-button-focusVisible {
            outline-color: var(--cds-color-interactive-stroke-primary-focus);
          }

          /* Style ghost CTAs with secondary variant styling for tablet and smaller */
          ${breakpoints.down('sm')} {
            padding: var(--cds-spacing-100) var(--cds-spacing-200);
            background-color: var(--cds-color-neutral-background-primary-weak);
            color: var(--cds-color-interactive-primary);
            box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary);

            &:hover {
              color: var(--cds-color-interactive-primary-hover);
              box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary-hover);
              background-color: var(--cds-color-interactive-background-primary-hover-weak);
            }

            .cds-button-focusVisible {
              outline: transparent solid 1px;
              outline-offset: 2px;
              box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary-hover);
              background-color: var(--cds-color-interactive-background-primary-hover-weak);
              color: var(--cds-color-interactive-primary-hover);
            }

            .cds-button-startIcon,
            .cds-button-endIcon {
              display: none;
            }

            span.cds-button-label {
              ${typography2.actionSecondary};
            }
          }
        }
      }
    }
  `,
};

export const CardTakeoverExperimentModuleRenderer = () => {
  const isRtl: boolean = isUserRightToLeft();
  const track = useTracker();
  const { courseraPlusProps, courseraForBusinessProps } = getCardTakeoverProps();

  const handleCourseraPlusClick = () => {
    track('click_merchandising_module', {
      merchandisingModule: {
        moduleType: 'promo_unit' as MerchandisingModuleModuleType,
        heading: courseraPlusProps.headline,
      },
      button: {
        name: 'promo_cta',
        linkType: 'internal',
        linkName: courseraPlusProps.ctaText,
        linkURL: courseraPlusProps.target,
      },
    });
  };

  const handleCourseraForBusinessClick = () => {
    track('click_merchandising_module', {
      merchandisingModule: {
        moduleType: 'promo_unit' as MerchandisingModuleModuleType,
        heading: courseraForBusinessProps.headline,
      },
      button: {
        name: 'promo_cta',
        linkType: 'internal',
        linkName: courseraForBusinessProps.ctaText,
        linkURL: courseraForBusinessProps.target,
      },
    });
  };

  const courseraPlusCard = { ...courseraPlusProps, onClick: handleCourseraPlusClick };
  const courseraForBusinessCard = { ...courseraForBusinessProps, onClick: handleCourseraForBusinessClick };
  return (
    <section css={styles.promoUnitsModule(isRtl)} data-testid="card-takeover-experiment-module">
      <PageGridContainer>
        <Grid item xs={12}>
          <div>
            <CannedCollectionModule
              cards={[courseraPlusCard, courseraForBusinessCard]}
              size="large"
              color="blue"
              showImageInFull={true}
              showAllEntries={true}
              variantConfig={{
                sm: 'grid',
              }}
            />
          </div>
        </Grid>
      </PageGridContainer>
    </section>
  );
};
